import { useCallback, useMemo, useRef, useState } from "react";
import { useToast } from "@hightouch/htui";
import type { DisclaimerSpec } from "@hightouch/lib/ad-studio/disclaimer/types";
import { MAX_VIDEO_BUNDLE_ENTRIES } from "@hightouch/lib/customer-data/video-export/bundle-types";
import {
  type SelectableAdAsset,
  selectVideoExportTargets,
} from "src/pages/ad-studio/briefs/hooks/bulk-video-download-targets";
import type { DownloadDialogProgress } from "src/pages/ad-studio/components/download-dialog";
import type {
  DownloadItemStatus,
  StaticDownloadProgress,
  VideoDownloadJob,
} from "src/pages/ad-studio/components/download-progress";

const IDLE_STATIC_PROGRESS: StaticDownloadProgress = {
  phase: "idle",
  items: [],
  fileCount: 0,
  warnings: [],
  error: null,
};

/** The cap counts renders, and an ad with size variants needs one per size. */
const OVER_LIMIT_TOAST = {
  id: "bulk-video-download-over-limit",
  title: "Too many videos selected",
  message: `You can download up to ${MAX_VIDEO_BUNDLE_ENTRIES} videos at a time, counting each size. Select fewer ads and try again.`,
  variant: "warning",
} as const;

/** How a static download reports into the progress dialog while it runs. */
export type StaticDownloadTracker = {
  begin: (items: Array<{ key: string; name: string }>) => void;
  setItemStatus: (key: string, status: DownloadItemStatus) => void;
  zipping: () => void;
  finish: (result: { fileCount: number; warnings: string[] }) => void;
  fail: (error: string, warnings?: string[]) => void;
  /** True once the dialog was closed mid-run; check it before triggering the download. */
  isCancelled: () => boolean;
};

function beginStaticProgress(
  items: Array<{ key: string; name: string }>,
): StaticDownloadProgress {
  return {
    ...IDLE_STATIC_PROGRESS,
    phase: "building",
    items: items.map((item) => ({ ...item, status: "pending" })),
  };
}

function withStaticItemStatus(
  progress: StaticDownloadProgress,
  key: string,
  status: DownloadItemStatus,
): StaticDownloadProgress {
  if (progress.phase === "idle") return progress;
  return {
    ...progress,
    items: progress.items.map((item) =>
      item.key === key ? { ...item, status } : item,
    ),
  };
}

/**
 * Owns a surface's download progress: the static zip's state, reported through
 * `staticTracker`, and the video batch started with `startVideos`. Either alone
 * makes `progress` non-null; the caller hands it to `DownloadDialog`.
 */
export function useDownloadProgress(args: {
  /** Base name for the video archive, without an extension. */
  videoFilename: string;
}) {
  const { videoFilename } = args;
  const { toast } = useToast();
  const [videoJob, setVideoJob] = useState<VideoDownloadJob | null>(null);
  const [staticProgress, setStaticProgress] =
    useState<StaticDownloadProgress>(IDLE_STATIC_PROGRESS);
  const cancelledRef = useRef(false);

  /**
   * The render cap, checkable before the export-settings dialog opens so an
   * over-limit selection doesn't collapse to a toast after the user confirms.
   * `startVideos` re-checks, so this is the early exit, not the only guard.
   */
  const isOverVideoRenderCap = useCallback(
    (renderCount: number): boolean => {
      if (renderCount <= MAX_VIDEO_BUNDLE_ENTRIES) return false;
      toast(OVER_LIMIT_TOAST);
      return true;
    },
    [toast],
  );

  /** Takes full asset records: the grids only carry the card fields. */
  const startVideos = useCallback(
    (assets: SelectableAdAsset[], spec: DisclaimerSpec) => {
      // The progress dialog enqueues renders once per mount, so replacing a
      // live job would leave the new targets unstarted behind a stale list.
      if (videoJob) {
        toast({
          id: "bulk-video-download-in-progress",
          title: "A video download is already running",
          message: "Wait for it to finish, or close it and try again.",
          variant: "warning",
        });
        return;
      }

      const selection = selectVideoExportTargets(assets);

      if (selection.targets.length === 0) {
        toast({
          id: "bulk-video-download-empty",
          title: "Nothing to download",
          message: "The selected videos have no saved version to export.",
          variant: "warning",
        });
        return;
      }

      if (selection.targets.length > MAX_VIDEO_BUNDLE_ENTRIES) {
        toast(OVER_LIMIT_TOAST);
        return;
      }

      setVideoJob({ selection, spec });
    },
    [toast, videoJob],
  );

  // Updates after the dialog closed find the idle state and leave it alone, so
  // a run that outlives its dialog can't reopen it.
  const staticTracker = useMemo<StaticDownloadTracker>(
    () => ({
      begin: (items) => {
        cancelledRef.current = false;
        setStaticProgress(beginStaticProgress(items));
      },
      setItemStatus: (key, status) =>
        setStaticProgress((prev) => withStaticItemStatus(prev, key, status)),
      zipping: () =>
        setStaticProgress((prev) =>
          prev.phase === "building" ? { ...prev, phase: "zipping" } : prev,
        ),
      finish: ({ fileCount, warnings }) =>
        setStaticProgress((prev) =>
          prev.phase === "idle"
            ? prev
            : { ...prev, phase: "done", fileCount, warnings },
        ),
      fail: (error, warnings = []) =>
        setStaticProgress((prev) =>
          prev.phase === "idle"
            ? prev
            : { ...prev, phase: "error", error, warnings },
        ),
      isCancelled: () => cancelledRef.current,
    }),
    [],
  );

  const closeProgress = useCallback(() => {
    cancelledRef.current = true;
    setVideoJob(null);
    setStaticProgress(IDLE_STATIC_PROGRESS);
  }, []);

  const progress: DownloadDialogProgress | null =
    videoJob || staticProgress.phase !== "idle"
      ? { staticProgress, videoJob, videoFilename }
      : null;

  return {
    startVideos,
    isOverVideoRenderCap,
    staticTracker,
    progress,
    closeProgress,
  };
}
